import React, { useState, useEffect, useRef } from "react";
import { Button, ListGroup, ProgressBar } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";

const SPICE_LEVELS = [
    { value: "mild", label: "1 マイルド (MILD)" },
    { value: "medium", label: "2 普通 (MEDIUM)" },
    { value: "hot", label: "3 中辛 (HOT)" },
    { value: "very_hot", label: "4 大辛 (VERY HOT)" },
    { value: "super_hot", label: "5 激辛 (SUPER HOT)" },
];

const SPICE_LEVELS1 = [
    { value: "mild",       title: "1 マイルド",     en_title: "1 MILD",       index: 1, icon: "" },
    { value: "medium",     title: "2 普通",          en_title: "2 MEDIUM",     index: 2, icon: "🌶🌶" },
    { value: "hot",        title: "3 中辛",          en_title: "3 HOT",        index: 3, icon: "🌶🌶🌶"},
    { value: "very_hot",   title: "4 大辛",          en_title: "4 VERY HOT",   index: 4, icon: "🌶🌶🌶🌶"},
    { value: "super_hot",  title: "5 激辛",          en_title: "5 SUPER HOT",  index: 5, icon: "🌶🌶🌶🌶🌶" }
];

const renderSpiceChili = (level) => {
    const index = SPICE_LEVELS.findIndex((l) => l.value === level);
    if (index === -1) return null;
    return "🌶".repeat(index + 1);
};

const CheckoutModal = ({ show, onClose, cart, handleConfirmOrder,ln}) => {
    const overlayVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
    const modalVariants = { hidden: { scale: 0.8, opacity: 0 }, visible: { scale: 1, opacity: 1 } };

    const [isConfirming, setIsConfirming] = useState(false);
    const [countdown, setCountdown] = useState(3);
    const intervalRef = useRef(null);
    const getSpiceTitle = (value) => {
        const item = SPICE_LEVELS1.find(level => level.value === value);
        return item ? (ln === 'en' ? item.en_title : item.title) : '';
    }

    const handleConfirm = () => {
        if (isConfirming || intervalRef.current) return;

        setIsConfirming(true);
        setCountdown(3);

        intervalRef.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    handleConfirmOrder();
                    setIsConfirming(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleCancelConfirm = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsConfirming(false);
        setCountdown(3);
    };

    useEffect(() => {
        if (!show && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            setIsConfirming(false);
            setCountdown(3);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [show]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{zIndex: 1070}}
                    variants={overlayVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                >
                    {/* Overlay */}
                    <motion.div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{background: "rgba(0,0,0,0.5)"}}
                        onClick={!isConfirming ? onClose : undefined}
                    />

                    {/* Modal */}
                    <motion.div
                        className="bg-white rounded shadow position-relative d-flex flex-column"
                        style={{
                            width: "90%",
                            maxWidth: "700px",
                            maxHeight: "90vh",
                            zIndex: 1080,
                            overflow: "hidden",
                            padding: "20px 10px"
                        }}
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                    >
                        <div style={{overflowY: "auto", flex: 1}}>
                            {!isConfirming ? (
                                <>
                                    <h4>{ln === 'jp' ? '注文の確認' : 'Order Review'}</h4>

                                    <ListGroup className="mb-3">
                                        {cart.map((c) => (
                                            <ListGroup.Item key={c.id}>
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <strong>{ln === 'en' ? c.title : c.jp}</strong> ({c.quantity})
                                                        {/* Single food spice */}
                                                        {c.type === "food" && c.spice_level && (
                                                            <div style={{color: "#ff4500", fontSize: "0.9rem"}}>
                                                                {renderSpiceChili(c.spice_level)}
                                                                {getSpiceTitle(c.spice_level)}
                                                            </div>
                                                        )}

                                                        {/* Set menu slots */}
                                                        {c.type === "set_menu" && c.slots?.length > 0 && (
                                                            <ListGroup className="mb-1 mt-1">
                                                                {c.slots.map((slot, idx) => (
                                                                    <ListGroup.Item key={idx}
                                                                                    className="py-1 px-2 small">
                                                                        <strong>{slot.slot_name}:</strong>
                                                                        {slot.chosen_foods.map((f, i) => (
                                                                            <div key={i}>
                                                                                {ln === 'en' ? f.name : f.japanese_name}{" "}
                                                                                {f.spice_level && (
                                                                                    <span style={{color: "#ff4500"}}>
                                                                                    {renderSpiceChili(f.spice_level)}
                                                                                        {getSpiceTitle(f.spice_level)}
                                                                                </span>
                                                                                )}
                                                                                {f.additional_cost > 0 &&
                                                                                    `( +¥${f.additional_cost.toFixed(2)} )`}
                                                                            </div>
                                                                        ))}
                                                                    </ListGroup.Item>
                                                                ))}
                                                            </ListGroup>
                                                        )}

                                                        <div>¥{(c.total_price).toFixed(2)}</div>
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>

                                    <h5>
                                        Total: ¥
                                        {cart.reduce((sum, item) => sum + item.total_price, 0).toFixed(2)}
                                    </h5>

                                    <div
                                        className="px-4 py-3 border-top bg-white"
                                        style={{
                                            position: "sticky",
                                            bottom: 0
                                        }}
                                    >
                                        <div className="d-flex justify-content-end">
                                            <Button variant="secondary" className="me-2" onClick={onClose}>
                                                Cancel
                                            </Button>
                                            <Button variant="primary" onClick={handleConfirm}>
                                                Confirm Order
                                            </Button>
                                        </div>
                                    </div>

                                </>
                            ) : (
                                <div className="text-center">
                                    <h5>✅ Your order will be placed in {countdown} seconds</h5>
                                    <p>You can cancel if you changed your mind.</p>
                                    <p>気が変わったらキャンセルできます.</p>
                                    <ProgressBar
                                        now={(3 - countdown) * (100 / 3)}
                                        label={`${3 - countdown}s`}
                                        className="mb-3"
                                    />
                                    <Button variant="danger" onClick={handleCancelConfirm}>
                                        Cancel Order / 注文をキャンセル
                                    </Button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CheckoutModal;
